import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { ToastContainer, Bounce } from "react-toastify";
import { FunkoFunBackground, Button } from "../../components";
import {
  ConventionForm,
  ConventionFormHeader,
  AddProductButton,
} from "./components";
import {
  useConventionForm,
  useWatchForm,
  useWatchConvention,
  useHandlers,
  useWatchFormAndConvention,
  useAdminLogin,
} from "./hooks";
import FormContext from "../../context/formContext";
import { fetchLiveConventions } from "../../store/liveConventions";
import { fetchConventions } from "../../store/conventionsSlice";
import "./index.scss";
import { getSelfServiceCnameSelector } from "../../store/selfServiceSlice";
import { ROUTES } from "../../utils/constants";

/**
 * @desc Self Service Page.
 */
const SelfService = () => {
  // CONSTANTS ----------------------------------------------------------------

  const dispatch = useDispatch();
  const cname = useSelector(getSelfServiceCnameSelector);

  // SIDE EFFECTS -------------------------------------------------------------

  /**
   * @desc Ensure admin is logged in.
   */
  const { isLoggedIn } = useAdminLogin();

  /**
   * @desc Only once, initialize the self service page.
   */
  useEffect(() => {
    dispatch(fetchConventions());
    dispatch(fetchLiveConventions());
  }, [dispatch]);

  // FORM STATE ---------------------------------------------------------------

  const {
    control,
    watch,
    setValue,
    setError,
    handleSubmit,
    resetField,
    reset,
    formState: { isSubmitting, isValid },
  } = useConventionForm({});

  /**
   * @desc Update form in store.
   */
  useWatchForm({ watch });

  /**
   * @desc Update form based on selected convention.
   */
  useWatchConvention({ watch, setValue, reset });

  const { difference, hasDifference } = useWatchFormAndConvention();

  const { onSave } = useHandlers({ difference });

  // COMPONENT ----------------------------------------------------------------

  if (!isLoggedIn) return;

  const context = {
    control,
    watch,
    setValue,
    setError,
    handleSubmit,
    resetField,
    reset,
  };

  const saveButtonProps = {
    disabled: !cname || isSubmitting || !isValid || !hasDifference,
    children: "SAVE",
    onClick: handleSubmit(onSave),
  };

  const toastContainerProps = {
    position: "top-center",
    autoClose: 3000,
    hideProgressBar: false,
    newestOnTop: false,
    closeOnClick: false,
    rtl: false,
    pauseOnFocusLoss: true,
    draggable: true,
    pauseOnHover: true,
    theme: "colored",
    transition: Bounce,
  };

  const previewButtonProps = {
    className: "funko-blue",
    disabled: !cname || isSubmitting,
  };

  return (
    <FormContext.Provider value={context}>
      <FunkoFunBackground>
        <ToastContainer {...toastContainerProps} />
        <div className="self-service funko">
          <h1>SELF SERVICE - CONVENTION TRACKER</h1>
          <ConventionFormHeader />
          <ConventionForm />
          <div className={"primary-buttons"}>
            <AddProductButton />
            <Button {...saveButtonProps} />
          </div>
          <Link className={"preview"} to={ROUTES.PREVIEW}>
            <Button {...previewButtonProps}>Preview</Button>
          </Link>
        </div>
      </FunkoFunBackground>
    </FormContext.Provider>
  );
};

export default SelfService;
